/**
 * Compras — vista del comprador.
 *
 * Dos secciones:
 *
 *   A) "Pendiente por comprar": tablero con 3 columnas (urgente / mediano plazo /
 *      largo plazo). Cada columna es una lista de tallos que faltan para cubrir
 *      las ventas abiertas. Incluye total global pendiente.
 *      Endpoint: GET /api/purchases/needs  → { buckets, totals }
 *      Cada fila tiene un selector para sobre-escribir su prioridad:
 *      POST /api/purchases/needs/priority { variety, stemLength, priority }
 *
 *   B) "Órdenes de compra": ExpandableCards con las POs creadas manualmente.
 *      Botón "Nueva OC" abre un modal donde el comprador asigna proveedor,
 *      fecha esperada, prioridad, items (variety/stemLength/quantity/priceUSD)
 *      y elige N ventas abiertas para ligar la OC (M:N).
 *      Las cajas NO aparecen aquí — a nivel de compras solo manejamos tallos.
 */
import { useMemo, useState } from 'react'
import {
  AlertTriangle,
  FileCheck2,
  Lock,
  Mail,
  Pencil,
  Plus,
  ShoppingCart,
  X,
  XCircle,
} from 'lucide-react'
import { toast } from 'sonner'
import {
  PURCHASE_PRIORITIES,
  PURCHASE_PRIORITY_LABELS,
  PURCHASE_CURRENCIES,
} from '@expogreens/shared'
import { ModuleShell } from '@/components/ui/ModuleShell'
import { Button } from '@/components/ui/Button'
import { Segmented } from '@/components/ui/Segmented'
import { FilterBar } from '@/components/ui/FilterBar'
import { ScrollX } from '@/components/ui/ScrollX'
import { SearchButton } from '@/components/ui/SearchButton'
import { ChoiceButtons } from '@/components/ui/ChoiceButtons'
import { Modal, Field, Input, Select } from '@/components/ui/Modal'
import { ExpandableCards } from '@/components/ui/ExpandableCards'
import { useApi } from '@/lib/useApi'
import { useMutation } from '@/lib/useMutation'
import { apiCall } from '@/lib/api'
import { fmtDate, fmtMoney } from '@/lib/utils'

/** Formato de cantidades con separador de miles (3000 → 3.000). */
const fmtQty = (n) => Number(n || 0).toLocaleString('es-CO')

// #44 — columnas "Pendiente por comprar" por prioridad. Cada columna es un
// bloque tintado (fondo suave + texto fuerte del mismo tono).
const NEEDS_COLUMN = {
  urgent: { title: 'Hoy y/o mañana', bg: '#FBE9E7', fg: '#D23B2E' },
  medium: { title: 'Esta semana', bg: '#FBF6D9', fg: '#8A7B1E' },
  long_term: { title: 'Próxima semana o +', bg: '#E3F0D5', fg: '#1E6E3A' },
}

// Etiquetas cortas para el filtro de prioridad (alineadas con las columnas).
const PRIORITY_SHORT = { urgent: 'Hoy/mañana', medium: 'Semana', long_term: 'Próxima +' }

// Badge de prioridad estilo handoff #43 para la lista de OCs.
const PRIORITY_BADGE = {
  urgent: { bg: '#FDECEC', fg: '#C0392B' },
  medium: { bg: '#FFF6E0', fg: '#B07D17' },
  long_term: { bg: '#E3F0D5', fg: '#1E6E3A' },
}
function priorityBadgeStyle(p) {
  const c = PRIORITY_BADGE[p] || { bg: '#F0F3EE', fg: '#5C6B60' }
  return {
    display: 'inline-flex',
    alignItems: 'center',
    background: c.bg,
    color: c.fg,
    fontSize: 13,
    fontWeight: 700,
    padding: '6px 14px',
    borderRadius: 999,
    lineHeight: 1,
    whiteSpace: 'nowrap',
  }
}

const EMPTY_PO_ITEM = { variety: '', quantity: 0, stemLength: 60, price: 0 }

export default function PurchasesPage() {
  const needs = useApi('/purchases/needs')
  const purchases = useApi('/purchases')
  const sales = useApi('/sales')
  const partners = useApi('/partners')
  const clients = useApi('/clients')
  const varieties = useApi('/varieties')

  const [creating, setCreating] = useState(false)
  // Cuando editingId !== null, el modal opera en modo edición (PUT) en vez
  // de creación (POST). El formulario es el mismo, solo cambia el handler.
  const [editingId, setEditingId] = useState(null)
  const [supplierId, setSupplierId] = useState('')
  const [supplierName, setSupplierName] = useState('')
  // Renombrado por feedback: "Fecha de Llegada" → "Fecha de Entrega".
  const [deliveryDate, setDeliveryDate] = useState(
    () => new Date().toISOString().slice(0, 10),
  )
  const [priority, setPriority] = useState('urgent')
  const [currency, setCurrency] = useState('COP')
  const [items, setItems] = useState([{ ...EMPTY_PO_ITEM }])
  const [linkedSaleIds, setLinkedSaleIds] = useState([])
  const [notes, setNotes] = useState('')

  // Filtros de la lista de OCs (sección B): prioridad (chips verdes) + búsqueda.
  const [poPriority, setPoPriority] = useState('all')
  const [poQuery, setPoQuery] = useState('')

  // ── Mapas auxiliares ──
  const clientsById = useMemo(() => {
    const m = new Map()
    for (const c of clients.data || []) m.set(c.id, c)
    return m
  }, [clients.data])

  const salesById = useMemo(() => {
    const m = new Map()
    for (const s of sales.data || []) m.set(s.id, s)
    return m
  }, [sales.data])

  /** Nombre del cliente para una venta (cliente final si hay jerarquía). */
  const clientLabelForSale = (sid) => {
    const s = salesById.get(sid)
    if (!s) return sid
    const c = clientsById.get(s.clientId)
    if (c?.name) return `${c.name}${s.dispatchTime ? ` · ${s.dispatchTime}` : ''}`
    return s.clientName || sid
  }

  // Lock de negocio replicado en frontend: una vez la OC está en cultivo
  // (at_farm) o completada, no se puede editar ni eliminar. La fuente de
  // verdad sigue siendo el backend, pero evitamos que el usuario dispare
  // requests condenados a 400.
  const PO_LOCKED_STATUSES = ['at_farm', 'completed']
  const isPoLocked = (po) => PO_LOCKED_STATUSES.includes(po.status)

  const create = useMutation((payload) =>
    apiCall('/purchases', { method: 'POST', body: payload }),
  )
  const update = useMutation(({ id, payload }) =>
    apiCall(`/purchases/${id}`, { method: 'PUT', body: payload }),
  )

  const suppliers = useMemo(
    () => (partners.data || []).filter((p) => p.type === 'supplier'),
    [partners.data],
  )

  const openSales = useMemo(
    () =>
      (sales.data || []).filter(
        (s) => !['delivered', 'cancelled'].includes(s.status),
      ),
    [sales.data],
  )

  const totalAmount = useMemo(
    () =>
      items.reduce(
        (acc, it) => acc + (Number(it.quantity) || 0) * (Number(it.price) || 0),
        0,
      ),
    [items],
  )

  // ── Alertas del formulario ──────────────────────────────────────────
  // 1) Fecha de entrega de la OC posterior a la fecha de despacho de
  //    cualquier venta ligada → riesgo de no llegar a tiempo.
  const lateDeliveryWarnings = useMemo(() => {
    if (!deliveryDate || linkedSaleIds.length === 0) return []
    const out = []
    for (const sid of linkedSaleIds) {
      const s = salesById.get(sid)
      const dispatch = s?.deliveryDate || s?.dispatchDate || s?.date
      if (!dispatch) continue
      if (new Date(deliveryDate) > new Date(dispatch)) {
        out.push({ sid, dispatch })
      }
    }
    return out
  }, [deliveryDate, linkedSaleIds, salesById])

  // 2) Precio de compra > precio promedio de venta de la misma variedad/longitud
  //    en alguna de las ventas ligadas → margen negativo. Solo aplica si las
  //    ventas tienen items con priceUSD.
  const priceWarnings = useMemo(() => {
    if (linkedSaleIds.length === 0) return []
    const refPrice = new Map() // key variety|stemLength → priceUSD max referencia
    for (const sid of linkedSaleIds) {
      const s = salesById.get(sid)
      if (!s) continue
      for (const it of s.items || []) {
        const k = `${it.variety}|${it.stemLength ?? ''}`
        const p = Number(it.priceUSD) || 0
        if (!p) continue
        refPrice.set(k, Math.max(refPrice.get(k) || 0, p))
      }
    }
    const warns = []
    for (const it of items) {
      const k = `${it.variety}|${it.stemLength ?? ''}`
      const ref = refPrice.get(k)
      // Si la OC es en COP, asumimos comparación aproximada vs USD * 4200.
      const buyUSD = currency === 'USD'
        ? Number(it.price) || 0
        : (Number(it.price) || 0) / 4200
      if (ref && buyUSD > ref) {
        warns.push({
          variety: it.variety,
          stemLength: it.stemLength,
          buyUSD,
          refUSD: ref,
        })
      }
    }
    return warns
  }, [items, linkedSaleIds, salesById, currency])

  const addItem = () => setItems((l) => [...l, { ...EMPTY_PO_ITEM }])
  const removeItem = (i) =>
    setItems((l) => (l.length > 1 ? l.filter((_, idx) => idx !== i) : l))
  const patchItem = (i, patch) =>
    setItems((l) => l.map((it, idx) => (idx === i ? { ...it, ...patch } : it)))

  const resetForm = () => {
    setEditingId(null)
    setSupplierId('')
    setSupplierName('')
    setDeliveryDate(new Date().toISOString().slice(0, 10))
    setPriority('urgent')
    setCurrency('COP')
    setItems([{ ...EMPTY_PO_ITEM }])
    setLinkedSaleIds([])
    setNotes('')
  }

  const openEdit = (po) => {
    if (isPoLocked(po)) {
      return toast.error('Esta OC ya fue entregada en cultivo y no es editable')
    }
    setEditingId(po.id)
    setSupplierId(po.supplierId || '')
    setSupplierName(po.supplierName || '')
    // Compat: campos viejos usaban `expectedAt`; ahora `deliveryDate`.
    setDeliveryDate(po.deliveryDate || po.expectedAt || new Date().toISOString().slice(0, 10))
    setPriority(po.priority || 'urgent')
    setCurrency(po.currency || 'COP')
    setItems(
      (po.items || []).length
        ? po.items.map((it) => ({
            variety: it.variety || '',
            quantity: Number(it.quantity) || 0,
            stemLength: Number(it.stemLength) || 60,
            // Compat con el campo viejo `priceUSD`.
            price: Number(it.price ?? it.priceUSD) || 0,
          }))
        : [{ ...EMPTY_PO_ITEM }],
    )
    setLinkedSaleIds(po.linkedSaleIds || [])
    setNotes(po.notes || '')
    setCreating(true)
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    if (!supplierId && !supplierName) {
      return toast.error('Indica proveedor (selecciona o escribe nombre)')
    }
    if (items.some((it) => !it.variety || Number(it.quantity) <= 0)) {
      return toast.error('Cada item necesita variedad y cantidad > 0')
    }
    const payload = {
      supplierId: supplierId || null,
      supplierName:
        supplierName || suppliers.find((s) => s.id === supplierId)?.name || '',
      // Doc: renombrado "Fecha de Llegada" → "Fecha de Entrega".
      deliveryDate,
      // Compat con consumidores que aún leen el campo viejo.
      expectedAt: deliveryDate,
      priority,
      currency,
      linkedSaleIds,
      items: items.map((it) => ({
        variety: it.variety,
        quantity: Number(it.quantity),
        stemLength: Number(it.stemLength) || 60,
        price: Number(it.price) || 0,
        // Compat: si la moneda es USD, también persistimos priceUSD.
        ...(currency === 'USD' ? { priceUSD: Number(it.price) || 0 } : {}),
      })),
      totalAmount,
      notes,
    }
    try {
      if (editingId) {
        await update.mutate({ id: editingId, payload })
        toast.success(`OC ${editingId} actualizada`)
      } else {
        await create.mutate({
          ...payload,
          date: new Date().toISOString().slice(0, 10),
        })
        toast.success('Orden de compra creada')
      }
      setCreating(false)
      resetForm()
      purchases.refresh()
      needs.refresh()
    } catch (err) {
      toast.error(err.message)
    }
  }

  // Doc: al cancelar una OC, el producto vuelve a "Pendiente por comprar"
  // y se debe poder escribir motivo + notificar al proveedor. Para esto el
  // backend ya tiene /api/purchases/:id/cancel — lo usamos aquí con un
  // prompt de motivo. La notificación por email queda registrada en el
  // estado de la OC (placeholder mientras se integra el envío real).
  const cancel = useMutation(({ id, reason }) =>
    apiCall(`/purchases/${id}/cancel`, { method: 'POST', body: { reason } }),
  )

  const onCancelPO = async (po) => {
    const reason = window.prompt(
      `Motivo de la cancelación de ${po.id} (se enviará al proveedor):`,
      '',
    )
    if (reason === null) return
    try {
      await cancel.mutate({ id: po.id, reason })
      toast.success(`OC ${po.id} cancelada. Producto vuelve a pendiente por comprar.`)
      purchases.refresh()
      needs.refresh()
    } catch (err) {
      toast.error(err.message)
    }
  }

  // Acciones "Enviar OC" / "Enviar Comprobante de Recepción". El envío
  // real por email queda pendiente de integración (se necesita servicio
  // SMTP/API). Por ahora se anota la marca de tiempo en la OC para que
  // quede trazabilidad de "se envió" — el backend ignora silenciosamente
  // los campos que no conoce, así que esto es seguro.
  const sendNotification = useMutation(({ id, kind }) =>
    apiCall(`/purchases/${id}`, {
      method: 'PUT',
      body: { [`${kind}SentAt`]: new Date().toISOString() },
    }),
  )

  const onSendOC = async (po) => {
    try {
      await sendNotification.mutate({ id: po.id, kind: 'oc' })
      toast.success(`OC ${po.id} enviada al proveedor (registrado)`)
      purchases.refresh()
    } catch (err) {
      toast.error(err.message)
    }
  }

  const onSendReceipt = async (po) => {
    try {
      await sendNotification.mutate({ id: po.id, kind: 'receipt' })
      toast.success(`Comprobante de recepción enviado para ${po.id}`)
      purchases.refresh()
    } catch (err) {
      toast.error(err.message)
    }
  }

  // Lista de OCs filtrada por prioridad + búsqueda (id / proveedor / venta).
  const filteredPOs = useMemo(() => {
    const list = purchases.data || []
    const q = poQuery.trim().toLowerCase()
    return list.filter((po) => {
      if (poPriority !== 'all' && po.priority !== poPriority) return false
      if (!q) return true
      const linked = (po.linkedSaleIds || [])
        .map((sid) => `${sid} ${clientLabelForSale(sid)}`)
        .join(' ')
      const hay = `${po.id || ''} ${po.supplierName || ''} ${linked}`.toLowerCase()
      return hay.includes(q)
    })
  }, [purchases.data, poPriority, poQuery, salesById, clientsById])

  return (
    <div>
      <ModuleShell
        title="Compras"
        subtitle="Pendiente por comprar + órdenes manuales al proveedor"
        icon={ShoppingCart}
        action={
          <div className="flex items-center gap-3">
            <SearchButton value={poQuery} onChange={setPoQuery} placeholder="Buscar OC o proveedor…" />
            <Button onClick={() => setCreating(true)} className="!px-3 sm:!px-6">
              <Plus size={20} />
              <span className="hidden sm:inline">Nueva OC</span>
            </Button>
          </div>
        }
        filters={
          purchases.data ? (
            <FilterBar left={
              <Segmented
                value={poPriority}
                onChange={setPoPriority}
                options={[
                  { value: 'all', label: 'Todas' },
                  ...PURCHASE_PRIORITIES.map((p) => ({ value: p, label: PRIORITY_SHORT[p] || PURCHASE_PRIORITY_LABELS[p] })),
                ]}
              />
            } />
          ) : undefined
        }
      >
       <div className="space-y-10">
      {/* ── Sección A: Pendiente por comprar (handoff #44) ────────── */}
      <section>
        {needs.loading && <p className="text-gray-500">Cargando…</p>}
        {needs.error && <p className="text-red-600">Error: {needs.error.message}</p>}
        {needs.data && (
          <div style={{ background: '#E9EEE3', borderRadius: 24, padding: '30px 30px 32px' }}>
            <div
              className="flex items-baseline justify-between flex-wrap"
              style={{ gap: 20, marginBottom: 22 }}
            >
              <div className="font-display" style={{ fontSize: 28, fontWeight: 800, color: '#143A22', lineHeight: 1, letterSpacing: '-0.01em' }}>
                Pendiente por comprar
              </div>
              <div style={{ fontSize: 16, fontWeight: 600, color: '#5C6B60' }}>
                Total pendiente:{' '}
                <span style={{ color: '#1E6E3A', fontWeight: 800 }}>
                  {fmtQty(needs.data.totals.total)} tallos
                </span>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-[18px] items-start">
              {PURCHASE_PRIORITIES.map((p) => {
                const rows = needs.data.buckets[p] || []
                const total = needs.data.totals[p] || 0
                const col = NEEDS_COLUMN[p] || NEEDS_COLUMN.urgent
                return (
                  <div key={p} style={{ background: col.bg, borderRadius: 18, padding: 18 }}>
                    <div
                      className="flex items-start justify-between"
                      style={{ gap: 12, marginBottom: 16 }}
                    >
                      <span className="font-display" style={{ fontSize: 18, fontWeight: 800, lineHeight: 1.2, color: col.fg }}>
                        {col.title}
                      </span>
                      <span style={{ fontSize: 16, fontWeight: 800, whiteSpace: 'nowrap', flex: 'none', color: col.fg }}>
                        {fmtQty(total)} tallos
                      </span>
                    </div>
                    <div style={{ minHeight: 180 }}>
                      {rows.length === 0 && (
                        <div
                          style={{
                            height: 180,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: col.fg,
                            opacity: 0.55,
                            fontSize: 15,
                            fontWeight: 500,
                          }}
                        >
                          Sin pendientes
                        </div>
                      )}
                      {rows.length > 0 && (
                        <div className="flex flex-col" style={{ gap: 14 }}>
                          {rows.map((r) => {
                            const needed = Number(r.needed) || 0
                            const buyPct = needed ? Math.min(100, ((Number(r.toBuy) || 0) / needed) * 100) : 100
                            return (
                              <div
                                key={`${r.variety}|${r.stemLength}`}
                                style={{ background: '#fff', borderRadius: 16, padding: '18px 20px', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}
                              >
                                <div className="flex items-start justify-between" style={{ gap: 12 }}>
                                  <div>
                                    <div className="font-display" style={{ fontSize: 21, fontWeight: 800, color: '#143A22', lineHeight: 1.1 }}>
                                      {r.variety}
                                    </div>
                                    <span
                                      className="inline-flex items-center"
                                      style={{ background: '#F2F4F0', color: '#5C6B60', fontSize: 13, fontWeight: 700, padding: '4px 11px', borderRadius: 8, marginTop: 9 }}
                                    >
                                      {r.stemLength}cm
                                    </span>
                                  </div>
                                  <div style={{ textAlign: 'right', flex: 'none' }}>
                                    <div style={{ fontSize: 30, fontWeight: 800, color: '#D23B2E', lineHeight: 1 }}>
                                      {fmtQty(r.toBuy)}
                                    </div>
                                    <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', color: '#CC9999', marginTop: 4 }}>
                                      POR COMPRAR
                                    </div>
                                  </div>
                                </div>
                                <div style={{ height: 7, borderRadius: 999, background: '#F3D9D6', overflow: 'hidden', margin: '16px 0 14px' }}>
                                  <div style={{ height: '100%', borderRadius: 999, background: '#E0382B', width: `${buyPct}%` }} />
                                </div>
                                <div className="flex items-center justify-between" style={{ fontSize: 14, marginBottom: 14 }}>
                                  <span style={{ color: '#7E8A80', fontWeight: 500 }}>
                                    Demanda <b style={{ color: '#143A22', fontWeight: 800 }}>{fmtQty(r.needed)}</b>
                                  </span>
                                  <span style={{ color: '#1E6E3A', fontWeight: 600 }}>
                                    Cubierto <b style={{ fontWeight: 800 }}>{fmtQty(r.fulfilled)}</b>
                                  </span>
                                </div>
                                <div style={{ borderTop: '1px solid #EFF2EC', paddingTop: 12 }}>
                                  <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', color: '#9BA89B', marginBottom: 4 }}>
                                    VENTAS
                                  </div>
                                  {(r.saleIds || []).map((sid) => (
                                    <div key={sid} className="truncate" style={{ fontSize: 13, color: '#3F4F44', fontWeight: 500 }}>
                                      <span style={{ color: '#1E6E3A', fontWeight: 700 }}>{sid}</span>{' '}
                                      {clientLabelForSale(sid)}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </section>

      {/* ── Sección B: Órdenes de compra ──────────────────────────── */}
      <section>
        <h2 className="font-display" style={{ fontSize: 22, fontWeight: 800, color: '#143A22', marginBottom: 14 }}>
          Órdenes de compra
        </h2>
        {purchases.loading && <p className="text-gray-500">Cargando…</p>}
        {purchases.error && <p className="text-red-600">Error: {purchases.error.message}</p>}
        {purchases.data && (
          <ExpandableCards
            items={filteredPOs}
            getKey={(r) => r.id}
            emptyText="Sin órdenes de compra"
            renderHeader={(r) => (
              <div className="flex items-center gap-3 flex-wrap">
                <span
                  className="font-display"
                  style={{ fontSize: 19, fontWeight: 800, color: '#143A22', letterSpacing: '-0.01em' }}
                >
                  {r.id}
                </span>
                <span style={{ fontSize: 16, fontWeight: 600, color: '#8B9B8F' }}>
                  {r.supplierName || '—'}
                </span>
                <span style={{ fontSize: 14, fontWeight: 600, color: '#9BA89B' }}>
                  entrega {fmtDate(r.deliveryDate || r.expectedAt)}
                </span>
                <span
                  className="ml-auto"
                  style={{ fontSize: 18, fontWeight: 800, color: '#143A22', whiteSpace: 'nowrap' }}
                >
                  {fmtQty((r.items || []).reduce((a, it) => a + (Number(it.quantity) || 0), 0))} tallos
                </span>
                <span style={priorityBadgeStyle(r.priority)}>
                  {PURCHASE_PRIORITY_LABELS[r.priority] || r.priority}
                </span>
                {r.status === 'cancelled' && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', background: '#FDECEC', color: '#C0392B', fontSize: 13, fontWeight: 700, padding: '6px 14px', borderRadius: 999, whiteSpace: 'nowrap' }}>
                    Cancelada
                  </span>
                )}
              </div>
            )}
            renderDetails={(r) => (
              <div className="space-y-6 pt-5">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-x-5 gap-y-5">
                  {[
                    ['Proveedor', r.supplierName],
                    ['Fecha de entrega', fmtDate(r.deliveryDate || r.expectedAt)],
                    ['Prioridad', PURCHASE_PRIORITY_LABELS[r.priority] || r.priority],
                    [`Total ${r.currency || 'USD'}`, fmtMoney(r.totalAmount ?? r.totalUSD)],
                  ].map(([label, value]) => (
                    <div key={label} className="min-w-0">
                      <div
                        className="uppercase"
                        style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.06em', color: '#9BA89B', marginBottom: 6 }}
                      >
                        {label}
                      </div>
                      <div className="break-words" style={{ fontSize: 18, fontWeight: 700, color: '#143A22' }}>
                        {value ?? '—'}
                      </div>
                    </div>
                  ))}
                </div>

                {r.linkedSaleIds && r.linkedSaleIds.length > 0 && (
                  <div>
                    <div className="font-display" style={{ fontSize: 18, fontWeight: 800, color: '#143A22', marginBottom: 12 }}>
                      Ventas ligadas
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {r.linkedSaleIds.map((sid) => (
                        <span
                          key={sid}
                          className="inline-flex items-center gap-2"
                          style={{ background: '#E3F0D5', borderRadius: 999, padding: '5px 12px', fontSize: 13 }}
                        >
                          <span style={{ color: '#1E6E3A', fontWeight: 800 }}>{sid}</span>
                          <span style={{ color: '#3F4F44', fontWeight: 600 }}>{clientLabelForSale(sid)}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <div className="font-display" style={{ fontSize: 18, fontWeight: 800, color: '#143A22', marginBottom: 12 }}>
                    Items
                  </div>
                  <div style={{ border: '1px solid #EEF2EC', borderRadius: 16, overflow: 'hidden', background: '#fff' }}>
                    <ScrollX fadeColor="#fff">
                    <div style={{ minWidth: 480 }}>
                    {(r.items || []).map((it, i) => (
                      <div
                        key={i}
                        className="grid items-center"
                        style={{
                          gridTemplateColumns: '1.6fr 1fr 1.2fr 1.2fr',
                          gap: 16,
                          padding: '15px 22px',
                          borderTop: i > 0 ? '1px solid #F2F5F0' : 'none',
                        }}
                      >
                        <span style={{ fontSize: 16, fontWeight: 700, color: '#143A22' }}>{it.variety}</span>
                        <span style={{ fontSize: 15, fontWeight: 600, color: '#5C6B60' }}>{it.stemLength}cm</span>
                        <span style={{ fontSize: 15, fontWeight: 600, color: '#5C6B60' }}>{fmtQty(it.quantity)} tallos</span>
                        <span style={{ fontSize: 15, fontWeight: 600, color: '#5C6B60', textAlign: 'right' }}>
                          {r.currency || 'USD'} {fmtMoney(it.price ?? it.priceUSD)}/u
                        </span>
                      </div>
                    ))}
                    </div>
                    </ScrollX>
                  </div>
                </div>

                {r.cancelReason && (
                  <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                    <span className="font-semibold">Motivo de cancelación: </span>
                    {r.cancelReason}
                  </div>
                )}

                <div className="flex items-center gap-2 flex-wrap pt-1">
                  {isPoLocked(r) ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Lock size={14} />
                      <span>Entregada en cultivo — no editable</span>
                    </div>
                  ) : r.status === 'cancelled' ? (
                    <div className="text-sm text-red-700">OC cancelada</div>
                  ) : (
                    <>
                      {/* 4 botones por feedback: Editar, Enviar OC,
                          Enviar Comprobante de Recepción, Cancelar. */}
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => openEdit(r)}
                      >
                        <Pencil size={16} />
                        <span>Editar</span>
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => onSendOC(r)}
                        disabled={sendNotification.loading}
                      >
                        <Mail size={16} />
                        <span>
                          Enviar OC{r.ocSentAt ? ' (reenviar)' : ''}
                        </span>
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => onSendReceipt(r)}
                        disabled={sendNotification.loading}
                      >
                        <FileCheck2 size={16} />
                        <span>
                          Enviar Comprobante de Recepción
                          {r.receiptSentAt ? ' (reenviar)' : ''}
                        </span>
                      </Button>
                      <Button
                        type="button"
                        variant="danger"
                        onClick={() => onCancelPO(r)}
                        disabled={cancel.loading}
                      >
                        <XCircle size={16} />
                        <span>Cancelar</span>
                      </Button>
                    </>
                  )}
                </div>
              </div>
            )}
          />
        )}
      </section>
      </div>
      </ModuleShell>

      {/* ── Modal: Nueva OC ────────────────────────────────────────── */}
      <Modal
        open={creating}
        onClose={() => {
          setCreating(false)
          resetForm()
        }}
        title={editingId ? `Editar ${editingId}` : 'Nueva orden de compra'}
        subtitle="Proveedor, items y ventas ligadas"
        size="xl"
      >
        <form onSubmit={onSubmit} className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Proveedor" required>
              <Select
                value={supplierId}
                onChange={(e) => setSupplierId(e.target.value)}
              >
                <option value="">— o escribe abajo —</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Proveedor (texto libre)">
              <Input
                value={supplierName}
                onChange={(e) => setSupplierName(e.target.value)}
                placeholder="Nombre proveedor"
              />
            </Field>
            <Field label="Fecha de entrega" required>
              <Input
                type="date"
                value={deliveryDate}
                onChange={(e) => setDeliveryDate(e.target.value)}
                required
              />
            </Field>
          </div>

          <Field label="Prioridad">
            <ChoiceButtons
              value={priority}
              onChange={setPriority}
              options={PURCHASE_PRIORITIES.map((p) => ({ value: p, label: PURCHASE_PRIORITY_LABELS[p] }))}
            />
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Moneda" hint="Por defecto COP/tallo. Cambia a USD si aplica.">
              <ChoiceButtons
                value={currency}
                onChange={setCurrency}
                options={PURCHASE_CURRENCIES.map((c) => ({ value: c, label: c }))}
              />
            </Field>
            <Field label="Notas">
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Opcional"
              />
            </Field>
          </div>

          {/* Alertas (fecha tardía vs despacho / precio compra > venta). */}
          {(lateDeliveryWarnings.length > 0 || priceWarnings.length > 0) && (
            <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 space-y-1">
              <div className="flex items-center gap-2 font-semibold">
                <AlertTriangle size={16} />
                Atención
              </div>
              {lateDeliveryWarnings.map((w) => (
                <div key={`late-${w.sid}`}>
                  Entrega ({fmtDate(deliveryDate)}) posterior al despacho de{' '}
                  <span className="font-mono">{w.sid}</span> ({fmtDate(w.dispatch)}).
                </div>
              ))}
              {priceWarnings.map((w, i) => (
                <div key={`price-${i}`}>
                  Precio de compra de <b>{w.variety} {w.stemLength}cm</b> (~USD {w.buyUSD.toFixed(2)})
                  es superior al de venta referencia (USD {w.refUSD.toFixed(2)}).
                </div>
              ))}
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-expo-dark">Items</span>
              <button
                type="button"
                onClick={addItem}
                className="text-sm text-expo-primary hover:text-expo-dark font-medium flex items-center gap-1"
              >
                <Plus size={16} />
                Añadir item
              </button>
            </div>
            <div className="space-y-2">
              {/* Column headers (matching col-spans below) */}
              <div className="grid grid-cols-12 gap-2 px-3 pb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                <div className="col-span-4">Variedad</div>
                <div className="col-span-2">Cantidad</div>
                <div className="col-span-2">Longitud (cm)</div>
                <div className="col-span-3">Precio ({currency}/tallo)</div>
                <div className="col-span-1" />
              </div>
              {items.map((it, idx) => (
                <div
                  key={idx}
                  className="grid grid-cols-12 gap-2 bg-expo-light/50 p-3 rounded-xl items-center"
                >
                  <div className="col-span-4">
                    <Select
                      value={it.variety}
                      onChange={(e) => patchItem(idx, { variety: e.target.value })}
                      required
                    >
                      <option value="">Variedad…</option>
                      {(varieties.data || []).map((v) => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Input
                      type="number"
                      min="0"
                      value={it.quantity}
                      onChange={(e) => patchItem(idx, { quantity: e.target.value })}
                      placeholder="Tallos"
                    />
                  </div>
                  <div className="col-span-2">
                    <Input
                      type="number"
                      min="0"
                      value={it.stemLength}
                      onChange={(e) => patchItem(idx, { stemLength: e.target.value })}
                      placeholder="cm"
                    />
                  </div>
                  <div className="col-span-3">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={it.price}
                      onChange={(e) => patchItem(idx, { price: e.target.value })}
                      placeholder={`${currency}/tallo`}
                    />
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <button
                      type="button"
                      onClick={() => removeItem(idx)}
                      disabled={items.length === 1}
                      className="p-2 rounded-lg text-red-600 hover:bg-red-50 disabled:opacity-30"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Field
            label="Ventas ligadas (M:N)"
            hint="Una OC puede cubrir varias ventas. Se muestra cliente y fecha de despacho."
          >
            <div className="border border-expo-line rounded-xl bg-white max-h-44 overflow-y-auto p-2 space-y-1">
              {openSales.length === 0 && (
                <div className="text-sm text-gray-400 text-center py-2">
                  Sin ventas abiertas
                </div>
              )}
              {openSales.map((s) => {
                const checked = linkedSaleIds.includes(s.id)
                const clientName =
                  clientsById.get(s.clientId)?.name || s.clientName || s.clientId
                return (
                  <label
                    key={s.id}
                    className="flex items-center gap-3 px-2 py-1.5 rounded-lg hover:bg-expo-light/60 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        setLinkedSaleIds((l) =>
                          e.target.checked ? [...l, s.id] : l.filter((x) => x !== s.id),
                        )
                      }}
                    />
                    <span className="text-sm font-mono text-expo-primary">{s.id}</span>
                    <span className="text-sm font-medium text-expo-dark">
                      {clientName}
                    </span>
                    <span className="text-xs text-gray-500 ml-auto">
                      despacha {fmtDate(s.deliveryDate || s.dispatchDate || s.date)}
                    </span>
                  </label>
                )
              })}
            </div>
          </Field>

          {/* Contador con variedades / longitudes / cantidades por comprar
              de las ventas seleccionadas — para referencia del comprador
              antes de meter los items. */}
          {linkedSaleIds.length > 0 && (
            <Field label="Resumen de tallos pendientes en las ventas seleccionadas">
              <div className="rounded-xl border border-expo-line bg-white divide-y divide-gray-100">
                {(() => {
                  const agg = new Map()
                  for (const sid of linkedSaleIds) {
                    const s = salesById.get(sid)
                    if (!s) continue
                    for (const it of s.items || []) {
                      const k = `${it.variety}|${it.stemLength ?? ''}`
                      if (!agg.has(k)) {
                        agg.set(k, {
                          variety: it.variety,
                          stemLength: it.stemLength,
                          quantity: 0,
                          sales: new Set(),
                        })
                      }
                      const row = agg.get(k)
                      row.quantity += Number(it.quantity) || 0
                      row.sales.add(sid)
                    }
                  }
                  const rows = Array.from(agg.values())
                  if (rows.length === 0) {
                    return (
                      <div className="text-sm text-gray-500 px-3 py-2">
                        Las ventas seleccionadas no tienen items con cantidades.
                      </div>
                    )
                  }
                  return rows.map((r, i) => (
                    <div key={i} className="px-3 py-2 text-sm flex items-center gap-3">
                      <span className="font-medium text-expo-dark">{r.variety}</span>
                      <span className="text-gray-500">{r.stemLength}cm</span>
                      <span className="font-bold ml-auto">{fmtQty(r.quantity)} tallos</span>
                      <span className="text-xs text-gray-500">
                        {Array.from(r.sales).join(', ')}
                      </span>
                    </div>
                  ))
                })()}
              </div>
            </Field>
          )}

          <div className="flex items-center justify-between pt-3 border-t border-expo-line">
            <span className="text-sm text-gray-600">Total OC</span>
            <span className="text-2xl font-bold text-expo-dark">
              {currency} {fmtMoney(totalAmount)}
            </span>
          </div>

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setCreating(false)
                resetForm()
              }}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={create.loading || update.loading}>
              {editingId
                ? update.loading
                  ? 'Guardando…'
                  : 'Guardar cambios'
                : create.loading
                  ? 'Creando…'
                  : 'Crear OC'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
